import { InlineError } from "@/components/QueryState";
import { SlackIcon } from "@/components/SlackIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

// Chief-of-staff Slack connect: a custom, manifest-created app with its own
// name (the exec's chief of staff, not the shared "Boo"), promoted over the
// shared app. Two prerequisites the shared path does not have: a name, and a
// one-time app-configuration token for the org's workspace.
export function CosSlackConnect({
  containerId,
  defaultName,
  disabled,
  onQuickConnect,
  quickPending,
}: {
  containerId: string | undefined;
  defaultName: string;
  disabled: boolean;
  onQuickConnect: () => void;
  quickPending: boolean;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(defaultName);
  const [token, setToken] = useState("");

  const workspace = useQuery({
    queryKey: ["slack-workspace"],
    queryFn: () => api.slack.custom.workspace.status(),
  });
  const linked = workspace.data?.connected === true;

  const link = useMutation({
    mutationFn: (refreshToken: string) => api.slack.custom.workspace.save(refreshToken),
    onSuccess: () => {
      setToken("");
      queryClient.invalidateQueries({ queryKey: ["slack-workspace"] });
    },
  });

  const install = useMutation({
    mutationFn: async (botName: string) => {
      if (!containerId) throw new Error("Your Boo isn't ready yet. Give it a moment.");
      const { url } = await api.slack.custom.install(containerId, botName, "/onboarding/connect");
      return url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "workspace_not_connected") {
        queryClient.invalidateQueries({ queryKey: ["slack-workspace"] });
      }
    },
  });

  const trimmedName = name.trim();
  const nameValid = trimmedName.length >= 2 && trimmedName.length <= 30;
  const trimmedToken = token.trim();
  const tokenValid = trimmedToken.startsWith("xoxe-") && trimmedToken.length > 20;
  const busy = disabled || install.isPending || link.isPending;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-muted/30 px-5 py-4">
      <div className="flex items-center gap-3">
        <SlackIcon className="size-7 shrink-0" />
        <div className="text-sm font-medium">Add to Slack</div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="cos-slack-name">Name in Slack</Label>
        <Input
          id="cos-slack-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={30}
          disabled={busy}
          aria-invalid={!nameValid ? true : undefined}
        />
      </div>

      {workspace.isSuccess && !linked && (
        <div className="space-y-1.5">
          <Label htmlFor="cos-slack-token">Workspace token</Label>
          <p className="text-xs text-muted-foreground">
            One time. At{" "}
            <a
              href="https://api.slack.com/apps"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              api.slack.com/apps
            </a>
            , open your profile menu, then App configuration tokens, then Generate.
          </p>
          <div className="flex gap-2">
            <Input
              id="cos-slack-token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="xoxe-1-…"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              disabled={busy}
            />
            <Button
              variant="outline"
              disabled={!tokenValid || busy}
              loading={link.isPending}
              onClick={() => link.mutate(trimmedToken)}
            >
              Link
            </Button>
          </div>
          {link.isError && <InlineError error={link.error} />}
        </div>
      )}
      {workspace.isError && <InlineError error={workspace.error} />}

      {install.isError && <InlineError error={install.error} />}

      <Button
        className="w-full"
        disabled={!nameValid || !linked || busy || !containerId}
        loading={install.isPending}
        onClick={() => install.mutate(trimmedName)}
      >
        Add to Slack
      </Button>
      <button
        type="button"
        className="text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        disabled={busy || quickPending}
        onClick={onQuickConnect}
      >
        {quickPending ? "Connecting…" : "Use the shared Boo app instead"}
      </button>
    </div>
  );
}
